import { useEffect, useCallback, useState, useRef } from 'react';
import useApi from './useApi';
import { ChangeSetBM, ChangeSetPagedResult } from '../types/ChangeSetTypes';
import { useAppSettings } from '../hooks/useAppSettings';
import { mapChangeSetBMs } from '../factory/ChangeSetFactory';
import { ChangeSet } from '../types/ChangeSetTypes';

interface GetChangeSetsParams {
    skip?: number;
    take?: number;
    sortBy?: string;
    sortOrder?: string;
    year?: number;
    displayInfo?: string;
}

export default () => {
    const { state, getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const [totalCount, setTotalCount] = useState<number>(0);
    const [allChangeSets, setAllChangeSets] = useState<ChangeSet[]>([]);
    const [loadedCount, setLoadedCount] = useState<number>(0);
    const isAppending = useRef(false);

    const getChangeSets = useCallback(
        async (params: GetChangeSetsParams = {}) => {
            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                const { skip = 0, take = 200, sortBy = 'DateTimeUtc', sortOrder = 'desc', year, displayInfo } = params;
                const queryParams = new URLSearchParams({
                    skip: skip.toString(),
                    take: take.toString(),
                    sortBy,
                    sortOrder,
                });

                if (year) {
                    queryParams.append('year', year.toString());
                }

                if (displayInfo) {
                    queryParams.append('displayInfo', displayInfo);
                }

                // Track whether this is an append (load more) or a fresh load
                isAppending.current = skip > 0;

                const fullUrl = `${apiUrl}GetChangeSets?${queryParams.toString()}`;
                await getApiData({
                    endpoint: fullUrl,
                });
            } catch (error) {
                console.error('[useGetChangeSets] Failed to fetch change sets:', error);
            }
        },
        [getApiData, appSettings]
    );

    useEffect(() => {
        if (state.status === 'success' && state.result) {
            const pagedResult = state.result as ChangeSetPagedResult;

            if (pagedResult && typeof pagedResult.totalCount === 'number') {
                setTotalCount(pagedResult.totalCount);
            }

            let newItems: ChangeSet[] = [];
            if (pagedResult && pagedResult.items && Array.isArray(pagedResult.items)) {
                newItems = mapChangeSetBMs(pagedResult.items);
            } else if (Array.isArray(state.result)) {
                newItems = mapChangeSetBMs(state.result as ChangeSetBM[]);
            }

            if (isAppending.current) {
                // Append to existing data (load more)
                setAllChangeSets((prev) => [...prev, ...newItems]);
                setLoadedCount((prev) => prev + newItems.length);
            } else {
                // Fresh load — replace all data
                setAllChangeSets(newItems);
                setLoadedCount(newItems.length);
            }
            isAppending.current = false;
        } else if (state.status === 'error') {
            console.error('[useGetChangeSets] Error:', state.error);
        }
    }, [state.status, state.result]);

    const reset = useCallback(() => {
        setAllChangeSets([]);
        setLoadedCount(0);
        setTotalCount(0);
    }, []);

    return {
        changeSets: allChangeSets,
        totalCount,
        loadedCount,
        loading: state.isLoading,
        error: state.error,
        getChangeSets,
        reset,
        status: state.status,
    };
};
